import { DocSourceOptionValues, OptionalReference } from './reference.type'
import { ResultTable } from './table.type'
import { Inference } from './inference.type'

import { FilterMetadata, SearchFilter } from '@/types/filter.type'
import { MessageSource } from '@/types/source'
import { MessageMeetingInformation } from '@/types/meeting_information.type'
import { FieldCurrencyInfo } from '@/constants/company_page'

// LLM model id for claude_workspace execution (routed via llm-gateway), e.g.
// 'claude-opus-4-8[1m]'. The selectable set is NOT known at build time — it is
// the DPC ModelPolicy allow-list served by alpha-server, so a model rollout is a
// DPC data change with no client deploy. Auto runs as a concrete id: the client
// snapshots the server default on first submit, then the conversation stays
// locked. Whether the user chose Auto is stored separately, in the first turn's
// filter_metadata.workspace_model_selection (ESS-59).
// See .claude/docs/workspace-model-selection.md.
export type WorkspaceModelId = string

export interface WorkspaceModelOption {
  value: WorkspaceModelId
  label: string
  short_label: string
  /** A Covered Model: not served under zero data retention, so the user has to
   *  agree before it may be pinned. Server-derived, so a new model family needs
   *  no client deploy. */
  requires_non_zdr_consent: boolean
}

export interface WorkspaceModelOptionsResponse {
  mode: string
  profile_slug: string
  policy_slug: string | null
  /** What a Default turn resolves to for this user (policy rules evaluated). */
  default_model: WorkspaceModelId | null
  /** Server-built e.g. "Default (Claude Opus 4.8)". The chip says "Auto", because
   *  the default is a routing decision rather than one named model — unless the
   *  organization has the `workspace_model_show_default_name` role, in which
   *  case it shows the model name unwrapped from this. */
  default_label: string
  /** Server-built short form, e.g. "Default (Opus 4.8)". Same rule for the
   *  chip's narrow layout. */
  default_short_label: string
  /** The current default model for display. Briefing modes include it even when
   *  policy prevents alternatives. Briefing Default sends null and follows the
   *  default model for each new run.
   *  Other modes omit it when enforced or outside the allow-list. Kept out of
   *  `options` so clients that still list `options` under their own Default
   *  entry do not show the default twice. */
  default_option?: WorkspaceModelOption | null
  /** Allowed models except default_model, newest first. The answer badge names
   *  models from this list plus `default_option`; the chip menu uses
   *  `menu_options`. */
  options: WorkspaceModelOption[]
  /** The chip menu under Auto: `options` plus `default_option`, newest model
   *  first regardless of which one is the default. Absent on older servers. */
  menu_options?: WorkspaceModelOption[]
  selection_allowed?: boolean
  selection_disabled_reason?: string | null
}

// The 1M-context marker analytics-server appends for Claude models. Stored and
// sent as part of the id; stripped only for display.
const ONE_M_SUFFIX = '[1m]'

export function stripContextSuffix(modelId: WorkspaceModelId): string {
  return modelId.endsWith(ONE_M_SUFFIX) ? modelId.slice(0, -ONE_M_SUFFIX.length) : modelId
}

export enum SelectableModel {
  LinqAlpha = 'linq_alpha',
  GPT4o = 'gpt-4o',
  News = 'news',
  AlphaWorkflow = 'alpha_workflow',
  CustomWorkflow = 'custom_workflow',
  IntegrateSearch = 'integrate_search',
  RMS = 'rms',
  RMSPro = 'rms_pro',
  Screener = 'screener',
  FunctionCall = 'function_call',
  CallSummarizer = 'call_summarizer',
  ReasoningR1 = 'reasoning_r1',
  RMSV2 = 'rms_v2',
  IntegrateSearchV2 = 'integrate_search_v2',
  CompanyDeepdive = 'company_deepdive',
  ArtifactChat = 'artifact_chat',
}

export const SelectableModelLabel: { [key in SelectableModel]: string } = {
  [SelectableModel.LinqAlpha]: 'Linq Alpha',
  [SelectableModel.GPT4o]: 'gpt-4o',
  [SelectableModel.News]: 'News',
  [SelectableModel.AlphaWorkflow]: 'Alpha Workflow',
  [SelectableModel.CustomWorkflow]: 'Custom Workflow',
  [SelectableModel.IntegrateSearch]: 'Integrate Search',
  [SelectableModel.RMS]: 'upload documents',
  [SelectableModel.RMSPro]: 'Workspace Pro',
  [SelectableModel.Screener]: 'Screener',
  [SelectableModel.FunctionCall]: 'Function Call',
  [SelectableModel.CallSummarizer]: 'Call Summarizer',
  [SelectableModel.ReasoningR1]: 'Reasoning R1',
  [SelectableModel.RMSV2]: 'RMS V2',
  [SelectableModel.IntegrateSearchV2]: 'Integrate Search V2',
  [SelectableModel.CompanyDeepdive]: 'Company DeepDive',
  [SelectableModel.ArtifactChat]: 'Artifact Chat',
}

export enum MessageSender {
  User = 'user',
  Assistant = 'assistant',
}

export interface SearchProcessItem {
  type: string
  value: any
}

export interface SearchStockChartData {
  stock_id: string
  name: string
  ticker: string
  start_date: string
  end_date: string
}

export interface SearchResult {
  search_process: SearchProcessItem[]
  stock_chart_data: SearchStockChartData[]
}

export interface Message {
  id: string
  conversation_id: string
  parent_message_id?: string
  parent_message?: Message
  content: string
  selected_model: SelectableModel
  sender: MessageSender
  created_at: string
  status: MessageStatus
  child_message?: Message
  filters?: SearchFilter
  // UI에 영향을 주는것들의 모음집 (Collection of values that affect the UI)
  filter_metadata?: FilterMetadata
  screener_preview_table?: ResultTable
  ownership_preview_table?: ResultTable
  function_mode?: FunctionMode
  source?: MessageSource
  meeting_information?: MessageMeetingInformation
  message_evaluation?: { id: string; evaluation: string; created_at: string; feedback?: string }
  inferences?: Inference[]
  workflow_result_url?: string
  onyx_chat_message_id?: string
  onyx_chat_session_id?: string
  follow_up_questions?: string[]
  search_result?: SearchResult
  trend_v2_pdf_url?: string
  trend_v2_raw_data_url?: string
  error?: string
  error_message?: string | null
  workspace_model?: WorkspaceModelId
  /** Show endpoint only: the server's call on whether the badge reads "Auto". */
  workspace_model_auto?: boolean | null
}

export interface InitMessageParams {
  selected_model: SelectableModel
  content: string
  sender: MessageSender
  status: MessageStatus
  created_at: string
  filters?: SearchFilter
  onyx_chat_session_id?: string
  function_mode?: FunctionMode
  optional_references?: OptionalReference[]
}

export interface CreateMessageParams {
  conversation_id: string
  parent_message_id?: string
  selected_model: SelectableModel
  content: string
  sender: MessageSender
  is_execute_search?: boolean
  filters?: SearchFilter
  // UI에 영향을 주는것들의 모음집 (Collection of values that affect the UI)
  filter_metadata?: FilterMetadata
  status?: MessageStatus
  input_tokens?: number
  output_tokens?: number
  start_time?: number
  time_to_first_byte?: number
  completion_time?: number
  function_content?: string
  function_mode?: FunctionMode
  optional_references?: OptionalReference[]
  workspace_model?: WorkspaceModelId
  dpc_mode?: string
  // Onyx assistant chat_message UUID. Forwarded for assistant child_message
  // creates so the column gets set without depending on the post-stream
  // complete_agent_chat webhook.
  onyx_chat_message_id?: string | null
}

export enum MessageStatus {
  Pending = 'pending',
  Success = 'success',
  SearchFailed = 'search_failed',
  SearchResultError = 'search_result_error',
  SearchResultFailed = 'search_result_failed',
  GenerateFailed = 'generate_failed',
  GenerateDuringFailed = 'generate_during_failed',
  ReasoningFailed = 'reasoning_failed',
  Searching = 'searching',
  URL_PROCESSING = 'url_processing',
  Stopped = 'stopped',
}

export const DefaultFilter = {
  stocks: [],
  doc_source: Object.values(DocSourceOptionValues),
}

export enum RmsFunctionCalls {
  Summarizer = 'summarizer',
  Timeline = 'timeline',
  DevilsAdvocate = 'devil_advocate',
  RecordingAgent = 'recording_agent',
}

export enum FunctionMode {
  IndividualSummary = 'individual_summary',
  OverallSummary = 'overall_summary',
  Timeline = 'timeline',
  DevilsAdvocate = 'devil_advocate',
  Normal = 'normal',
  NormalGPT5 = 'normal_gpt5',
  NormalGPT5_1 = 'normal_gpt5_1',
  NormalGPT5_2 = 'normal_gpt5_2',
  NormalGemini3Pro = 'normal_gemini_3_pro',
  RecordingAgent = 'recording_agent',
  MeetingAgent = 'meeting_agent',
  CallSummarize = 'call_summarize',
  CompanyScreener = 'company_screener',
  OwnershipData = 'ownership_data',
  StructuredData = 'structured_data',
  RmsV2Pro = 'rms_v2_pro',
  RmsV2Reasoning = 'rms_v2_reasoning',
  RmsV2Research = 'rms_v2_research',
  DeepResearch = 'deep_research',
  DeepResearch5_1 = 'deep_research5_1',
  Primer = 'primer',
  PulseCheck = 'pulse_check',
  CatalystSearch = 'catalyst_search',
  CompanyPage = 'company_page',
  RedditSentiment = 'reddit_sentiment',
  TrendV2 = 'trend_v2',
  ClaudeWorkspace = 'claude_workspace',
}

export type PrimerFormParams = {
  stock: {
    id: string
    ticker: string
    labelText: string
    name: string
  }
  country: {
    id: string
    labelText: string
  }
}

export interface TfsPrimerParams {
  filters: {
    stock_id: string
    ticker: string
    ticker_name: string
  }
  conversation_id: string
  start_time?: string
}

export interface TfsPulseCheckParams {
  filters: {
    theme?: string
    ticker?: string[]
    indices?: string[]
    category: string
    start_date: string
    end_date: string
    stock_ids?: string[]
    industries?: string[]
    keywords_of_mention?: string
  }
  conversation_id: string
  start_time?: string
}

export interface RegenerateMessageParams {
  id: string
  content: string
}

export interface UpdateMessageFollowUpQuestionsParams {
  id: string
  query: string
  answer: string
}

export interface UpdateMessageParams {
  id: string
  onyx_chat_message_id?: string
}

//  Company Page 관련 타입 정의

export enum CompanyPageWorkflowStatus {
  Completed = 'completed',
  Failed = 'failed',
  Pending = 'pending',
}

export interface StructuredDataRow {
  date: string
  values: Record<string, string>
}

export interface StructuredDataTable {
  title: string
  rows: StructuredDataRow[]
}

export interface StructuredDataInterval {
  interval: string
  tables: StructuredDataTable[]
}

export interface StructuredDataResponse {
  one_liner: string
  sections: StructuredDataInterval[]
  currency_info: Array<{
    stock_id: string
    currency: string
    exchange_rate: number
  }>
  field_currency_info: FieldCurrencyInfo
  stock_name_mapping: Record<string, string>
}

// 메인 타입
export type StructuredData = StructuredDataInterval[]

export interface CompanyPageWorkflowData {
  final_output: {
    company_overview: string
    segment_breakdown: string
    revenue_and_margin_drivers: string
  }
  section_type: string
  chat_message_uuid: string
  fiscal_year: number
  fiscal_quarter: number
}

export interface CompanyPageWorkflowItem {
  id: string
  workflow_type: string
  workflow_version: string
  user_id: string | null
  organization_id: string | null
  stock_id: string
  fiscal_year: number
  fiscal_quarter: number
  event_datetime: string | null
  input_data: {
    mock?: boolean
    stock_id: string
    fiscal_year: number
    fiscal_quarter: number
  }
  output_data: {
    final_output: {
      company_overview: string
      segment_breakdown: string
      revenue_and_margin_drivers: string
    }
    chat_message_uuid: string
  }
  status: CompanyPageWorkflowStatus
  error_message: string | null
  error_id: string | null
  sent: boolean
  time_sent: string | null
  execution_duration: number
  created_at: string
  updated_at: string
  initial_execute: boolean
}

export interface CompanyPageBatchResults {
  [key: string]: CompanyPageWorkflowItem
}

export interface EarningsHistoryStatus {
  status: string
  execution_id: string
  created_at: string
  updated_at: string
  execution_duration: number | null
  error_message: string | null
  error_id: string | null
}

export interface EarningsHistoryQuarter {
  fiscal_year: number
  fiscal_quarter: number
  guidance_change: EarningsHistoryStatus
  earnings_history: EarningsHistoryStatus
  bull_bear: EarningsHistoryStatus
}

export interface EarningsHistoryResponse {
  stock_id: string
  latest_fiscal_year: number
  latest_fiscal_quarter: number
  earnings_release_date: string
  quarters: EarningsHistoryQuarter[]
}

export interface CompanyPageTimelineItem {
  article_id: string
  result: string
  published_at: string
  link?: string
}

export interface CompanyPageTimelineResponse {
  timeline_items: CompanyPageTimelineItem[]
  success: boolean
  error_message: string
}
